/**
 * Pagination — simple Prev/Next with "showing X-Y of Z" line.
 *
 * Reused on Positions·Closed and Signals·History pages. No page-number
 * buttons — the dataset is reverse-chronological so users only ever
 * walk forward/back; jumping to page 47 is not a real workflow.
 */
import { useTranslation } from 'react-i18next'
import { Button } from './Button'

interface PaginationProps {
  total: number
  limit: number
  offset: number
  onChange: (offset: number) => void
  loading?: boolean
}

export function Pagination({
  total, limit, offset, onChange, loading,
}: PaginationProps) {
  const { t } = useTranslation('app')
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + limit, total)
  const canPrev = offset > 0
  const canNext = offset + limit < total

  return (
    <div className="flex items-center gap-3 text-xs text-textFaint font-mono px-5 py-3 border-t border-line">
      <span>
        {t('pagination.showing', { from, to, total })}
      </span>
      <div className="ml-auto flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={!canPrev || loading}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          {t('pagination.prev')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!canNext || loading}
          onClick={() => onChange(offset + limit)}
        >
          {t('pagination.next')}
        </Button>
      </div>
    </div>
  )
}
